/**
 * Created Sprint: 打磨輪 · 棒 31-237 E5(new file)
 * Last contract-affecting Sprint: 打磨輪 · 棒 31-237 E5(new file;FCE-S5-ASSETCLASS-001 / 判例 EZ)
 * Freeze status: frozen(打磨輪 freeze #8,2026-10-17;SPRINT_FREEZE_LEDGER.md 的 `freeze #8` 節)——
 *   棒 31-243 依 freeze 判定改寫(乙類:新增管制);登記欄不動 —— flip 本身不是 contract-affecting 改動。
 * Purpose: 媒體格式的單一來源(副檔名 ↔ MIME ↔ 大類);網頁端與圖片 worker 自它導出;OBS-195。
 *   - 大類(MEDIA_CLASSES)= 資料庫 asset_references.asset_type 的封閉值域(圖片 · 音訊 · 影片 · 文件;migration 20261016120000)。
 *     細分格式不加欄位,走 storage_key 的副檔名 + 本表。
 *   - MEDIA_FORMATS 只列可以上傳的格式:圖片 4 · 音訊 6(值照棒 31-237 之前圖片 worker 的 ALLOWED_MIME_TYPES / MIME_TO_EXT)·
 *     文件 1(application/pdf)。影片零項 —— 值域開了,上傳先不開(Owner 2026-10-11)。
 *   - noteAttachment = 記事附件收的格式(音訊六項與 PDF;Owner 2026-10-14);圖片照舊走圖片節點,不是附件。
 *   - 多個 MIME 共用一個副檔名的照舊:audio/mp4 · audio/x-m4a → m4a;audio/wav · audio/x-wav → wav。
 *   - PERMANENTLY_EXCLUDED_MIMES:永久排除(會被瀏覽器當成主動內容的 html · xhtml · svg · JavaScript,與可執行檔)—— 只給測試與防呆;
 *     本表零項。加非主動內容的新格式只改本表,不再開 FCE(Owner 2026-10-11)。
 *   - isNoteAttachmentStorageKey:記事附件的 storage_key 是否為本產品鑄的形(棒 31-237 E13 第 9 則;附件 node 的 parseHTML 與附件檢視用)。
 *   🔴 零 import、零依賴:圖片 worker 以相對路徑 import 本檔(workers/image/index.ts),網頁端經 @kiroku/shared 的 barrel。
 *   對帳:apps/web/src/lib/mediaFormats.consistency.test.ts(兩份名單分歧 ⇒ CI 的全套測試紅)。
 *
 * Related Docs: docs/plans/r3b-fix-round/handoff/baton-31-237-attachments.md §3 E5;hard-rules.md §5(asset_type 封閉集合)
 */

/** 資料庫的附件大類(asset_references.asset_type 的封閉值域;順序照 CHECK 的寫法)。 */
export const MEDIA_CLASSES = ['image', 'audio', 'video', 'document'] as const;
export type MediaClass = (typeof MEDIA_CLASSES)[number];

export interface MediaFormat {
  /** MIME(上傳表單欄位的 type;R2 的 contentType)。 */
  readonly mime: string;
  /** storage_key 的副檔名(小寫,不帶點)。 */
  readonly ext: string;
  /** 資料庫的大類。 */
  readonly cls: MediaClass;
  /** 記事附件收不收這個格式。 */
  readonly noteAttachment: boolean;
}

/** 可以上傳的格式(唯讀;影片零項)。 */
export const MEDIA_FORMATS: readonly MediaFormat[] = [
  { mime: 'image/jpeg', ext: 'jpg', cls: 'image', noteAttachment: false },
  { mime: 'image/png', ext: 'png', cls: 'image', noteAttachment: false },
  { mime: 'image/webp', ext: 'webp', cls: 'image', noteAttachment: false },
  { mime: 'image/avif', ext: 'avif', cls: 'image', noteAttachment: false },
  { mime: 'audio/mpeg', ext: 'mp3', cls: 'audio', noteAttachment: true },
  { mime: 'audio/ogg', ext: 'ogg', cls: 'audio', noteAttachment: true },
  { mime: 'audio/mp4', ext: 'm4a', cls: 'audio', noteAttachment: true },
  { mime: 'audio/x-m4a', ext: 'm4a', cls: 'audio', noteAttachment: true },
  { mime: 'audio/wav', ext: 'wav', cls: 'audio', noteAttachment: true },
  { mime: 'audio/x-wav', ext: 'wav', cls: 'audio', noteAttachment: true },
  { mime: 'application/pdf', ext: 'pdf', cls: 'document', noteAttachment: true },
];

/** 永久排除(主動內容與可執行檔;只給測試與防呆 —— 本表零項)。 */
export const PERMANENTLY_EXCLUDED_MIMES: readonly string[] = [
  'text/html',
  'application/xhtml+xml',
  'image/svg+xml',
  'text/javascript',
  'application/javascript',
  'application/x-javascript',
  'application/ecmascript',
  'text/ecmascript',
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/vnd.microsoft.portable-executable',
  'application/x-executable',
  'application/x-elf',
  'application/x-mach-binary',
  'application/x-sh',
  'application/x-csh',
  'application/x-bat',
  'application/java-archive',
  'application/vnd.android.package-archive',
];

/** 可以上傳的 MIME(格式表的全部項,順序照表)。 */
export function uploadableMimes(): string[] {
  return MEDIA_FORMATS.map((f) => f.mime);
}

/** 某一大類的 MIME(順序照表;影片 ⇒ 空陣列)。 */
export function mimesOfClass(cls: MediaClass): string[] {
  return MEDIA_FORMATS.filter((f) => f.cls === cls).map((f) => f.mime);
}

/** MIME 的副檔名;不在表 ⇒ null。 */
export function extForMime(mime: string): string | null {
  return MEDIA_FORMATS.find((f) => f.mime === mime)?.ext ?? null;
}

/** MIME 的大類;不在表 ⇒ null。 */
export function classForMime(mime: string): MediaClass | null {
  return MEDIA_FORMATS.find((f) => f.mime === mime)?.cls ?? null;
}

/** 副檔名正規化:去掉開頭的點、轉小寫。 */
const normExt = (ext: string): string => ext.replace(/^\./, '').toLowerCase();

/** 副檔名的大類(瀏覽器給的 file.type 是空字串時用;大小寫不分,帶不帶點都收);不在表 ⇒ null。 */
export function classForExt(ext: string): MediaClass | null {
  const e = normExt(ext);
  return MEDIA_FORMATS.find((f) => f.ext === e)?.cls ?? null;
}

/** 副檔名在表中的第一個 MIME(file.type 是空字串時用);不在表 ⇒ null。 */
export function mimeForExt(ext: string): string | null {
  const e = normExt(ext);
  return MEDIA_FORMATS.find((f) => f.ext === e)?.mime ?? null;
}

/** 記事附件的副檔名(noteAttachment 那幾項;去重)。 */
const NOTE_ATTACHMENT_EXTS: ReadonlySet<string> = new Set(MEDIA_FORMATS.filter((f) => f.noteAttachment).map((f) => f.ext));

/**
 * 記事附件的 storage_key 是不是本產品自己鑄的形:`users/<使用者 id>/<64 位小寫十六進位>.<記事附件的副檔名>`
 * (網頁端 buildImageStorageKey 的形;雜湊 = 內容的 sha256)。
 * 棒 31-237 E13 第 9 則:貼上的 HTML 能造出 src 為外部網址或任意路徑的附件 —— 附件 node 的 parseHTML 只收這一形,
 * 附件檢視對不合形的鍵零載入。
 */
export function isNoteAttachmentStorageKey(key: unknown): key is string {
  if (typeof key !== 'string') return false;
  const m = /^users\/[A-Za-z0-9_-]+\/[0-9a-f]{64}\.([a-z0-9]+)$/.exec(key);
  return m !== null && NOTE_ATTACHMENT_EXTS.has(m[1]!);
}

/** 記事附件的 `<input accept>` 字串:記事附件收的 MIME(順序照表)與它們的副檔名(去重),以逗號接起來。 */
export const NOTE_ATTACHMENT_ACCEPT: string = (() => {
  const formats = MEDIA_FORMATS.filter((f) => f.noteAttachment);
  const exts = [...new Set(formats.map((f) => `.${f.ext}`))];
  return [...formats.map((f) => f.mime), ...exts].join(',');
})();
